import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import { ISequenceStep, DEFAULT_AI_LANGUAGE } from '../../../../interfaces/models/campaign';
import {
  CAMPAIGN_TEMPLATE_VARIABLES,
  CampaignVariableKey,
  ICampaignVariableFallbacks,
  extractVariablesFromText,
  unrecognizedVariableTokens,
  variableToken,
} from '../../../../utils/campaignTemplateVariables';
import VariableFallbackPopover from './VariableFallbackPopover';
import { CampaignVariableWarnBanner } from './CampaignVariableWarnModal';
import CampaignComposeActions, { CampaignStepTools } from './CampaignComposeActions';
import CampaignGeneratePopup from './CampaignGeneratePopup';
import useReplaceUndo from './useReplaceUndo';
import TooltipWrapper from '../../../../components/TooltipWrapper';

/**
 * InMail / Open Profile composer — same layout contract as CampaignEmailComposer:
 * one Language + Preview for the step, a subject with only a variable affordance,
 * and a body with Generate / template / clear / undo.
 */

interface Props {
  step: ISequenceStep;
  onChange: (patch: Partial<ISequenceStep>) => void;
  fallbacks: ICampaignVariableFallbacks;
  onFallbackChange: (key: CampaignVariableKey, value: string, saveAsUserDefault?: boolean) => void;
  campaignDescription?: string;
  senderName?: string;
  showErrors?: boolean;
  readOnly?: boolean;
}

const CampaignSnMessageComposer: React.FC<Props> = ({
  step, onChange, fallbacks, onFallbackChange, campaignDescription, senderName,
  showErrors = false, readOnly,
}) => {
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const subjectVarBtnRef = useRef<HTMLButtonElement>(null);
  /** Last caret in subject — clicking `{ }` blurs the input and can reset selection to 0. */
  const subjectCaretRef = useRef<{ start: number; end: number } | null>(null);
  const [varMenuFor, setVarMenuFor] = useState<'subject' | 'body' | null>(null);
  const [subjectMenuPos, setSubjectMenuPos] = useState<{
    top: number; left: number; place: 'above' | 'below';
  } | null>(null);
  const [dialog, setDialog] = useState<'generate' | 'preview' | null>(null);
  const [popover, setPopover] = useState<
    { key: CampaignVariableKey; anchorRect: DOMRect } | null
  >(null);

  const legacy = step.messageMode === 'ai' && !(step.messageTemplate ?? '').trim();
  const subjectValue = step.messageSubject ?? '';
  const bodyValue = (step.messageTemplate ?? '') || (legacy ? (step.messageAiPrompt ?? '') : '');

  const rememberSubjectCaret = useCallback(() => {
    const el = subjectRef.current;
    if (!el) return;
    subjectCaretRef.current = {
      start: el.selectionStart ?? subjectValue.length,
      end: el.selectionEnd ?? subjectValue.length,
    };
  }, [subjectValue.length]);

  const writeSubject = useCallback((text: string) => {
    onChange({ messageSubject: text });
  }, [onChange]);

  const writeBody = useCallback((text: string) => {
    onChange({ messageTemplate: text, messageMode: 'template', finalContent: '' });
  }, [onChange]);

  const draft = useMemo(
    () => ({ subject: subjectValue, body: bodyValue }),
    [subjectValue, bodyValue],
  );

  const writeDraft = useCallback((next: { subject: string; body: string }) => {
    if (next.subject !== subjectValue) writeSubject(next.subject);
    if (next.body !== bodyValue) writeBody(next.body);
  }, [subjectValue, bodyValue, writeSubject, writeBody]);

  const draftUndo = useReplaceUndo(
    draft,
    writeDraft,
    (a, b) => a.subject === b.subject && a.body === b.body,
  );

  const insertVariable = useCallback((key: CampaignVariableKey, target: 'subject' | 'body') => {
    setVarMenuFor(null);
    setSubjectMenuPos(null);
    const token = variableToken(key);
    if (target === 'subject') {
      const el = subjectRef.current;
      const saved = subjectCaretRef.current;
      // Prefer remembered caret; live selection only if input is still focused.
      // If nothing was saved (never focused), append — same as email composer.
      const live = document.activeElement === el;
      const start = saved != null
        ? saved.start
        : (live ? (el?.selectionStart ?? subjectValue.length) : subjectValue.length);
      const end = saved != null
        ? saved.end
        : (live ? (el?.selectionEnd ?? subjectValue.length) : subjectValue.length);
      const next = `${subjectValue.slice(0, start)}${token}${subjectValue.slice(end)}`;
      writeSubject(next);
      subjectCaretRef.current = { start: start + token.length, end: start + token.length };
      requestAnimationFrame(() => {
        const caret = Math.min(start + token.length, next.length);
        el?.focus();
        el?.setSelectionRange(caret, caret);
      });
      return;
    }
    const el = bodyRef.current;
    const start = el?.selectionStart ?? bodyValue.length;
    const end = el?.selectionEnd ?? bodyValue.length;
    const next = `${bodyValue.slice(0, start)}${token}${bodyValue.slice(end)}`;
    writeBody(next);
    requestAnimationFrame(() => {
      const caret = Math.min(start + token.length, next.length);
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
  }, [subjectValue, bodyValue, writeSubject, writeBody]);

  const usedVariables = useMemo(
    () => extractVariablesFromText(`${subjectValue}\n${bodyValue}`),
    [subjectValue, bodyValue],
  );
  const [badTokens, setBadTokens] = useState<string[]>([]);
  useEffect(() => {
    const timer = window.setTimeout(
      () => setBadTokens(unrecognizedVariableTokens(`${subjectValue}\n${bodyValue}`)),
      500,
    );
    return () => window.clearTimeout(timer);
  }, [subjectValue, bodyValue]);

  const bodyEmpty = !bodyValue.trim();
  const subjectEmpty = !subjectValue.trim();

  const openSubjectVarMenu = useCallback(() => {
    if (varMenuFor === 'subject') {
      setVarMenuFor(null);
      setSubjectMenuPos(null);
      return;
    }
    const el = subjectVarBtnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const menuH = 220;
    const gap = 6;
    const spaceBelow = window.innerHeight - r.bottom - gap;
    const spaceAbove = r.top - gap;
    const place: 'above' | 'below' =
      spaceBelow < menuH && spaceAbove > spaceBelow ? 'above' : 'below';
    setSubjectMenuPos({
      top: place === 'below' ? r.bottom + gap : r.top - gap,
      left: Math.max(8, Math.min(r.right - 160, window.innerWidth - 168)),
      place,
    });
    setVarMenuFor('subject');
  }, [varMenuFor]);

  const closeVarMenus = useCallback(() => {
    setVarMenuFor(null);
    setSubjectMenuPos(null);
  }, []);

  if (readOnly) return null;

  return (
    <Wrap>
      {/* One Language + Preview for the whole step (same as email composer). */}
      <CampaignStepTools
        language={step.aiLanguage || DEFAULT_AI_LANGUAGE}
        onLanguageChange={lang => onChange({ aiLanguage: lang })}
        languageStepType="sn_message"
        onPreview={() => setDialog('preview')}
        previewDisabled={bodyEmpty && subjectEmpty}
      />

      {legacy && (
        <LegacyNote>
          This step still writes a new message for every lead. Editing the text below,
          or taking a generated draft, switches it to one message for everyone.
        </LegacyNote>
      )}

      <Field>
        <FieldHead>
          <FieldLabel>Subject <ReqMark>*</ReqMark></FieldLabel>
        </FieldHead>
        <InputRow>
          <SubjectInput
            ref={subjectRef}
            $invalid={showErrors && subjectEmpty}
            value={subjectValue}
            placeholder="Enter a custom subject line for this InMail"
            onChange={e => writeSubject(e.target.value)}
            onSelect={rememberSubjectCaret}
            onKeyUp={rememberSubjectCaret}
            onClick={rememberSubjectCaret}
            onKeyDown={draftUndo.onKeyDown}
          />
          <InputVarBtn
            ref={subjectVarBtnRef}
            type="button"
            title="Insert variable"
            onMouseDown={e => {
              // Keep subject focused so selection is not wiped before we snapshot it.
              e.preventDefault();
              rememberSubjectCaret();
            }}
            onClick={openSubjectVarMenu}
          >
            {'{ }'}
          </InputVarBtn>
        </InputRow>
      </Field>

      <Field>
        <FieldHead>
          <FieldLabel>Message <ReqMark>*</ReqMark></FieldLabel>
          <FieldHeadSpacer />
          <Chars>{bodyValue.length} characters</Chars>
        </FieldHead>

        <Shell $invalid={showErrors && bodyEmpty}>
          <Area
            ref={bodyRef}
            rows={5}
            value={bodyValue}
            placeholder={'Write your Open Profile / InMail message, or describe what you want AI to generate.\n\ne.g. Hi {{firstName}}, I came across your profile at {{company}} and wanted to reach out…'}
            onChange={e => writeBody(e.target.value)}
            onKeyDown={draftUndo.onKeyDown}
          />
          <Toolbar>
            <TBtn
              type="button"
              title="Clear message"
              disabled={bodyEmpty}
              onClick={() => draftUndo.replace({ subject: subjectValue, body: '' })}
            >
              <ClearIcon />
            </TBtn>
            <TooltipWrapper tooltipText="Undo — restore what was in the field (Ctrl+Z)" placement="top">
              <span>
                <TBtn
                  type="button"
                  title="Undo"
                  disabled={!draftUndo.canUndo}
                  onClick={draftUndo.undo}
                  aria-label="Undo replacement"
                >
                  <UndoIcon />
                </TBtn>
              </span>
            </TooltipWrapper>
            <ToolbarSpacer />
            <VarButton
              type="button"
              onClick={() => setVarMenuFor(varMenuFor === 'body' ? null : 'body')}
            >
              Insert variable <Caret>▾</Caret>
              {varMenuFor === 'body' && (
                <VarMenu
                  $right
                  $up
                  onMouseDown={e => e.preventDefault()}
                  onClick={e => e.stopPropagation()}
                >
                  {CAMPAIGN_TEMPLATE_VARIABLES.map(v => (
                    <VarItem
                      key={v.key}
                      type="button"
                      onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                      onClick={e => { e.stopPropagation(); insertVariable(v.key, 'body'); }}
                    >
                      {v.token}
                    </VarItem>
                  ))}
                </VarMenu>
              )}
            </VarButton>
          </Toolbar>
        </Shell>

        <ActionsWrap>
          <CampaignComposeActions
            generateDisabled={bodyEmpty}
            onGenerate={() => setDialog('generate')}
            onInsertTemplate={content => draftUndo.replace({ subject: subjectValue, body: content })}
          />
        </ActionsWrap>
      </Field>

      <CampaignVariableWarnBanner tokens={badTokens} />

      {usedVariables.length > 0 && (
        <UsedRow>
          <UsedLabel>Fallback values:</UsedLabel>
          {usedVariables.map(key => {
            const has = !!(fallbacks[key] ?? '').trim();
            return (
              <UsedPill
                key={key}
                type="button"
                $missing={!has}
                onClick={e => setPopover({
                  key,
                  anchorRect: e.currentTarget.getBoundingClientRect(),
                })}
              >
                {variableToken(key)}{has && <FallbackHint>→ {fallbacks[key]}</FallbackHint>}
              </UsedPill>
            );
          })}
        </UsedRow>
      )}

      <CampaignGeneratePopup
        show={dialog !== null}
        mode={dialog === 'preview' ? 'preview' : 'generate'}
        onHide={() => setDialog(null)}
        stepType="message"
        withSubject
        instruction={bodyValue}
        language={step.aiLanguage || DEFAULT_AI_LANGUAGE}
        campaignDescription={campaignDescription}
        variableFallbacks={fallbacks}
        senderName={senderName}
        senderSubline="LinkedIn InMail"
        subject={subjectValue}
        onUse={(text, generatedSubject) => draftUndo.replace({
          subject: generatedSubject || subjectValue,
          body: text,
        })}
      />

      {popover && (
        <VariableFallbackPopover
          variableKey={popover.key}
          value={fallbacks[popover.key] ?? ''}
          anchorRect={popover.anchorRect}
          mode="edit"
          onSave={(val, asDefault) => { onFallbackChange(popover.key, val, asDefault); setPopover(null); }}
          onClose={() => setPopover(null)}
        />
      )}

      {varMenuFor === 'subject' && subjectMenuPos && createPortal(
        <>
          <MenuScrim $elevated onClick={closeVarMenus} />
          <VarMenuPortal
            $place={subjectMenuPos.place}
            style={{ top: subjectMenuPos.top, left: subjectMenuPos.left }}
            onMouseDown={e => e.preventDefault()}
          >
            {CAMPAIGN_TEMPLATE_VARIABLES.map(v => (
              <VarItem
                key={v.key}
                type="button"
                onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                onClick={e => { e.stopPropagation(); insertVariable(v.key, 'subject'); }}
              >
                {v.token}
              </VarItem>
            ))}
          </VarMenuPortal>
        </>,
        document.body,
      )}

      {/* z-index 20 — same as email — so the in-toolbar VarMenu (z-index 30) stays clickable */}
      {varMenuFor === 'body' && <MenuScrim onClick={closeVarMenus} />}
    </Wrap>
  );
};

export default CampaignSnMessageComposer;

const ClearIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
    <path d="M4 7h16" /><path d="M10 7V5h4v2" /><path d="M6 7l1 13h10l1-13" />
  </svg>
);

const UndoIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
    <path d="M3 8h11a6 6 0 010 12H8" />
    <path d="M3 8l4-4M3 8l4 4" />
  </svg>
);

const ACCENT = styles.colors.primary600;

const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 14px; width: 100%;
`;

const LegacyNote = styled.div`
  border: 1px solid #fcd34d; background: #fffbeb; border-radius: 8px;
  padding: 8px 10px;
  font-family: NunitoSans; font-size: 11px; line-height: 1.5; color: #92400e;
`;

const Field = styled.div`
  display: flex; flex-direction: column; gap: 6px; width: 100%;
`;

const FieldHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
`;

const FieldHeadSpacer = styled.div`flex: 1 1 auto;`;

const FieldLabel = styled.span`
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: #334155;
`;

const ReqMark = styled.span`color: #ef4444;`;

const Chars = styled.span`
  font-family: NunitoSans; font-size: 11px; color: #94a3b8;
`;

const InputRow = styled.div`
  position: relative;
  display: flex; align-items: stretch; width: 100%;
`;

const SubjectInput = styled.input<{ $invalid?: boolean }>`
  flex: 1 1 auto; min-width: 0; box-sizing: border-box;
  border: 1.5px solid ${p => (p.$invalid ? '#fca5a5' : '#e2e8f0')};
  border-radius: 10px; padding: 11px 38px 11px 13px; background: #fff;
  font-family: NunitoSans; font-size: 13px; color: #0f172a;
  &:focus { outline: none; border-color: ${ACCENT}; }
  &::placeholder { color: #b6c0cd; }
`;

const InputVarBtn = styled.button`
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; border-radius: 6px;
  border: none; background: none; cursor: pointer;
  font-family: 'SF Mono', Menlo, monospace; font-size: 12px; color: #64748b;
  &:hover { background: #f1f5f9; color: ${ACCENT}; }
`;

const Shell = styled.div<{ $invalid?: boolean }>`
  border: 1.5px solid ${p => (p.$invalid ? '#fca5a5' : '#e2e8f0')};
  border-radius: 10px; background: #fff;
  overflow: visible;
  transition: border-color 0.12s;
  &:focus-within { border-color: ${ACCENT}; }
`;

const Area = styled.textarea`
  display: block; width: 100%; box-sizing: border-box;
  border: none; background: none; resize: vertical;
  padding: 12px 14px;
  font-family: NunitoSans, sans-serif; font-size: 13px; line-height: 1.6;
  color: ${styles.colors.black600};
  &:focus { outline: none; }
  &::placeholder { color: #b6bfcc; }
`;

const Toolbar = styled.div`
  display: flex; align-items: center; gap: 4px;
  padding: 6px 8px;
  border-top: 1px solid #eef1f6;
`;

const ToolbarSpacer = styled.div`flex: 1 1 auto;`;

const TBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 6px;
  border: none; background: none; cursor: pointer; color: #64748b;
  &:hover:not(:disabled) { background: #f1f5f9; color: ${ACCENT}; }
  &:disabled { opacity: .4; cursor: default; }
`;

const VarButton = styled.button`
  position: relative;
  flex: 0 0 auto;
  display: inline-flex; align-items: center; gap: 4px;
  border: none; background: transparent;
  border-radius: 8px; padding: 4px 6px;
  cursor: pointer;
  font-family: NunitoSans; font-size: 12px; font-weight: 600; color: #475569;
  &:hover { color: ${ACCENT}; }
`;

const Caret = styled.span`font-size: 9px; margin-left: 2px;`;

const VarMenu = styled.div<{ $right?: boolean; $up?: boolean }>`
  position: absolute;
  ${p => (p.$up ? 'bottom: calc(100% + 6px);' : 'top: calc(100% + 6px);')}
  ${p => (p.$right ? 'right: 0;' : 'left: 0;')}
  z-index: 30;
  min-width: 160px;
  padding: 4px;
  border: 1px solid #e2e8f0; border-radius: 10px;
  background: #fff; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
`;

const VarMenuPortal = styled.div<{ $place: 'above' | 'below' }>`
  position: fixed;
  z-index: 10000;
  transform: ${({ $place }) => ($place === 'above' ? 'translateY(-100%)' : 'none')};
  min-width: 160px;
  padding: 4px;
  border: 1px solid #e2e8f0; border-radius: 10px;
  background: #fff;
  box-shadow: ${({ $place }) => (
    $place === 'above'
      ? '0 -8px 24px rgba(15, 23, 42, 0.12)'
      : '0 8px 24px rgba(15, 23, 42, 0.12)'
  )};
`;

const VarItem = styled.button`
  display: block; width: 100%; text-align: left;
  border: none; background: none; border-radius: 6px;
  padding: 7px 10px; cursor: pointer;
  font-family: NunitoSans; font-size: 12px; color: #334155;
  &:hover { background: #f1f5f9; color: ${ACCENT}; }
`;

const ActionsWrap = styled.div`
  margin-top: 2px;
`;

const UsedRow = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
`;

const UsedLabel = styled.span`
  font-family: NunitoSans; font-size: 11px; color: #64748b;
`;

const UsedPill = styled.button<{ $missing?: boolean }>`
  display: inline-flex; align-items: center; gap: 4px;
  border: 1px solid ${p => (p.$missing ? '#fbbf24' : '#e2e8f0')};
  background: ${p => (p.$missing ? '#fffbeb' : '#f8fafc')};
  border-radius: 999px; padding: 3px 9px; cursor: pointer;
  font-family: NunitoSans; font-size: 11px; color: #475569;
`;

const FallbackHint = styled.span`color: #94a3b8;`;

const MenuScrim = styled.div<{ $elevated?: boolean }>`
  position: fixed; inset: 0;
  z-index: ${({ $elevated }) => ($elevated ? 9999 : 20)};
`;
